import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import AnimatedSection from '../components/common/AnimatedSection';
import { constitutionData, organicLawsData } from '../data/constitution';
import { 
  Search, Copy, Link2, Printer, ChevronRight, ChevronDown, 
  ChevronUp, Menu, X, Check, BookOpen, Scale, FileText,
  Vote, RefreshCw, Users, Sparkles, ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';

const ConstitutionPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isTocOpen, setIsTocOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [activeSection, setActiveSection] = useState('preambule');
  const contentRef = useRef(null);

  // Scroll to anchor on mount
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection(hash);
        }
      }, 500);
    }
  }, [location.hash]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section-id]');
      let currentSection = 'preambule';
      
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150) {
          currentSection = section.getAttribute('data-section-id');
        }
      });
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Build searchable index
  const searchIndex = useMemo(() => {
    const index = [];
    
    // Add preamble
    index.push({
      id: constitutionData.preamble.id,
      type: 'preamble',
      title: 'Préambule',
      text: constitutionData.preamble.content
    });
    
    // Add constitution articles
    constitutionData.titles.forEach((title) => {
      if (title.articles) {
        title.articles.forEach((article) => {
          index.push({
            id: article.id,
            type: 'constitution',
            title: `Article ${article.number}`,
            parent: `Titre ${title.number} : ${title.title}`,
            text: article.text
          });
        });
      }
      if (title.chapters) {
        title.chapters.forEach((chapter) => {
          chapter.articles.forEach((article) => {
            index.push({
              id: article.id,
              type: 'constitution',
              title: `Article ${article.number}`,
              parent: `Titre ${title.number}, Chapitre ${chapter.number} : ${chapter.title}`,
              text: article.text
            });
          });
        });
      }
    });
    
    // Add organic laws articles
    organicLawsData.forEach((law) => {
      if (law.articles && law.articles.length > 0) {
        law.articles.forEach((article) => {
          index.push({
            id: article.id,
            type: 'organic-law',
            title: `Article ${article.number}`,
            parent: `Loi organique n°${law.number} : ${law.title}`,
            text: article.text
          });
        });
      }
      if (law.chapters) {
        law.chapters.forEach((chapter) => {
          chapter.articles.forEach((article) => {
            index.push({
              id: article.id,
              type: 'organic-law',
              title: `Article ${article.number}`,
              parent: `Loi n°${law.number}, Chapitre ${chapter.number} : ${chapter.title}`,
              text: article.text
            });
          });
        });
      }
    });
    
    return index;
  }, []);

  // Search function
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    const lowerQuery = query.toLowerCase();
    const results = searchIndex.filter((item) => 
      item.text.toLowerCase().includes(lowerQuery) ||
      item.title.toLowerCase().includes(lowerQuery)
    ).slice(0, 20);
    
    setSearchResults(results);
  }, [searchIndex]);

  // Copy functions
  const copyLink = async (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(`link-${id}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const copyArticle = async (id, text, title) => {
    const fullText = `${title}\n${text}\n\nSource: Constitution de la 4ᵉ République de Côte d'Ivoire`;
    await navigator.clipboard.writeText(fullText);
    setCopiedId(`text-${id}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  // Jump to result
  const jumpToResult = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
      setSearchQuery('');
      setSearchResults([]);
      setIsSearching(false);
      setIsTocOpen(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Highlight text
  const highlightText = (text, query) => {
    if (!query || query.length < 2) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-[#F5A623]/30 px-1 rounded">{part}</mark> : part
    );
  };

  // Article component
  const ArticleBlock = ({ article, parentTitle }) => (
    <div 
      id={article.id}
      data-section-id={article.id}
      className={`py-6 border-b last:border-0 scroll-mt-24 transition-all ${
        isDark ? 'border-white/10' : 'border-[#E8E0D4]'
      } ${
        activeSection === article.id 
          ? isDark ? 'bg-[#5B2D8E]/10 -mx-4 px-4 rounded-lg' : 'bg-[#5B2D8E]/5 -mx-4 px-4 rounded-lg' 
          : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h4 className={`font-semibold mb-2 text-lg ${isDark ? 'text-[#7C4DAF]' : 'text-[#5B2D8E]'}`}>
            Article {article.number}
          </h4>
          <p className="text-theme-secondary leading-relaxed text-base" style={{ fontFamily: 'var(--font-serif)' }}>
            {highlightText(article.text, searchQuery)}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => copyLink(article.id)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-stone-400 hover:text-[#FFC107] hover:bg-white/5' : 'text-[#6B6B6B] hover:text-[#5B2D8E] hover:bg-[#5B2D8E]/5'}`}
            title="Copier le lien"
          >
            {copiedId === `link-${article.id}` ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => copyArticle(article.id, article.text, `Article ${article.number}`)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-stone-400 hover:text-[#FFC107] hover:bg-white/5' : 'text-[#6B6B6B] hover:text-[#5B2D8E] hover:bg-[#5B2D8E]/5'}`}
            title="Copier l'article"
          >
            {copiedId === `text-${article.id}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`pt-24 min-h-screen ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
      {/* Breadcrumb */}
      <div className={`border-b print:hidden ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-[#F5F0E8] border-[#E8E0D4]'}`}>
        <div className="container mx-auto px-4 lg:px-10 py-3">
          <nav className={`flex items-center gap-2 text-sm ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>
            <Link to="/" className={`hover:${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className={`font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>Textes officiels</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-theme-primary">Constitution 4ᵉ République</span>
          </nav>
        </div>
      </div>

      {/* Header with Search */}
      <div className="bg-[#5B2D8E] text-white py-12 print:bg-white print:text-black">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-8 h-8 text-[#F5A623]" />
              <span className="text-[#F5A623] text-sm font-medium uppercase tracking-wider">Textes officiels</span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              {constitutionData.title}
            </h1>
            <p className="text-xl text-white/80" style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
              {constitutionData.subtitle}
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-2xl relative print:hidden">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6B6B]" />
              <input
                type="text"
                placeholder="Rechercher un article, mot-clé..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white text-[#2D2D2D] rounded-xl border-0 focus:ring-2 focus:ring-[#F5A623] placeholder:text-[#9A9A9A]"
              />
            </div>
            
            {/* Search Results Dropdown */}
            {isSearching && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-[#E8E0D4] max-h-96 overflow-y-auto z-50">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => jumpToResult(result.id)}
                    className="w-full text-left px-4 py-3 hover:bg-[#F5F0E8] border-b border-[#E8E0D4] last:border-0"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {result.type === 'constitution' ? (
                        <BookOpen className="w-4 h-4 text-[#5B2D8E]" />
                      ) : (
                        <FileText className="w-4 h-4 text-[#F5A623]" />
                      )}
                      <span className="font-medium text-[#2D2D2D]">{result.title}</span>
                    </div>
                    {result.parent && (
                      <p className="text-xs text-[#6B6B6B] mb-1">{result.parent}</p>
                    )}
                    <p className="text-sm text-[#6B6B6B] line-clamp-2">
                      {highlightText(result.text.substring(0, 150) + '...', searchQuery)}
                    </p>
                  </button>
                ))}
              </div>
            )}
            {isSearching && searchResults.length === 0 && searchQuery.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-[#E8E0D4] p-4 text-center text-[#6B6B6B]">
                Aucun résultat trouvé pour « {searchQuery} »
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comparison CTA - Before Constitution Content */}
      <div className={`py-6 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="flex items-center justify-center">
            <Link
              to="/comparaison"
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-lg font-semibold transition-all hover:-translate-y-1 border-2 ${
                isDark 
                  ? 'border-[#F7C948] bg-[#F7C948]/10 text-[#F7C948] hover:bg-[#F7C948]/20' 
                  : 'border-[#5B2D8E] bg-[#5B2D8E]/5 text-[#5B2D8E] hover:bg-[#5B2D8E]/10'
              }`}
            >
              <Scale className="w-5 h-5" />
              {language === 'fr' ? 'Comparer avec la 3ᵉ République' : language === 'en' ? 'Compare with the 3rd Republic' : 'Comparar com a 3ª República'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="container mx-auto px-4 lg:px-10 py-8">
        <div className="flex gap-8">
          {/* Mobile TOC Toggle */}
          <button
            onClick={() => setIsTocOpen(true)}
            className="lg:hidden fixed bottom-6 right-6 z-40 bg-[#5B2D8E] text-white p-4 rounded-full shadow-lg print:hidden"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Table of Contents - Sidebar */}
          <aside className={`
            fixed lg:sticky top-0 lg:top-24 left-0 h-full lg:h-[calc(100vh-6rem)] w-80 lg:w-72 
            z-50 lg:z-0 transform transition-transform duration-300
            ${isTocOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            overflow-y-auto border-r lg:border-0 print:hidden
            ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-[#E8E0D4]'}
          `}>
            <div className="p-6 lg:p-0">
              {/* Mobile Close */}
              <div className="flex items-center justify-between mb-6 lg:hidden">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-[#2D2D2D]'}`}>Sommaire</h3>
                <button onClick={() => setIsTocOpen(false)} className="p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Print Button */}
              <button
                onClick={handlePrint}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors mb-6 ${
                  isDark ? 'bg-[#292524] text-[#FFC107] hover:bg-[#3D3836]' : 'bg-[#F5F0E8] text-[#5B2D8E] hover:bg-[#E8E0D4]'
                }`}
              >
                <Printer className="w-4 h-4" />
                Imprimer / Export PDF
              </button>

              {/* TOC */}
              <nav className="space-y-1">
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>Constitution</h4>
                
                {/* Preamble */}
                <a
                  href="#preambule"
                  onClick={() => setIsTocOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === 'preambule' 
                      ? 'bg-[#5B2D8E] text-white' 
                      : isDark ? 'text-stone-300 hover:bg-white/10' : 'text-[#2D2D2D] hover:bg-[#F5F0E8]'
                  }`}
                >
                  Préambule
                </a>

                {/* Titles */}
                {constitutionData.titles.map((title) => (
                  <div key={title.id}>
                    <button
                      onClick={() => toggleSection(title.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection.startsWith(title.id) || activeSection.startsWith('article-')
                          ? isDark ? 'text-[#FFC107] font-medium' : 'text-[#5B2D8E] font-medium'
                          : isDark ? 'text-stone-300 hover:bg-white/10' : 'text-[#2D2D2D] hover:bg-[#F5F0E8]'
                      }`}
                    >
                      <span>Titre {title.number}</span>
                      {expandedSections[title.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {expandedSections[title.id] && (
                      <div className={`ml-4 mt-1 space-y-1 border-l-2 pl-3 ${isDark ? 'border-white/10' : 'border-[#E8E0D4]'}`}>
                        <a
                          href={`#${title.id}`}
                          onClick={() => setIsTocOpen(false)}
                          className={`block py-1 text-xs ${isDark ? 'text-stone-400 hover:text-[#FFC107]' : 'text-[#6B6B6B] hover:text-[#5B2D8E]'}`}
                        >
                          {title.title}
                        </a>
                        {title.chapters?.map((chapter) => (
                          <a
                            key={chapter.id}
                            href={`#${chapter.id}`}
                            onClick={() => setIsTocOpen(false)}
                            className={`block py-1 text-xs ${isDark ? 'text-stone-400 hover:text-[#FFC107]' : 'text-[#6B6B6B] hover:text-[#5B2D8E]'}`}
                          >
                            Chap. {chapter.number} : {chapter.title}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className={`pt-4 mt-4 border-t ${isDark ? 'border-white/10' : 'border-[#E8E0D4]'}`}>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>Lois Organiques</h4>
                  {organicLawsData.map((law) => (
                    <a
                      key={law.id}
                      href={`#${law.id}`}
                      onClick={() => setIsTocOpen(false)}
                      className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === law.id || activeSection.startsWith(`${law.id}-`)
                          ? 'bg-[#F5A623]/10 text-[#D4880D]' 
                          : isDark ? 'text-stone-300 hover:bg-white/10' : 'text-[#2D2D2D] hover:bg-[#F5F0E8]'
                      }`}
                    >
                      N°{law.number} — {law.title.length > 25 ? law.title.substring(0, 25) + '...' : law.title}
                    </a>
                  ))}
                </div>
              </nav>
            </div>
          </aside>

          {/* Overlay for mobile */}
          {isTocOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden print:hidden"
              onClick={() => setIsTocOpen(false)}
            />
          )}

          {/* Main Content */}
          <main ref={contentRef} className="flex-1 max-w-4xl">
            {/* Preamble */}
            <section 
              id="preambule" 
              data-section-id="preambule"
              className={`rounded-xl border p-8 mb-8 scroll-mt-24 ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-[#E8E0D4]'}`}
            >
              <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-[#7C4DAF]' : 'text-[#5B2D8E]'}`} style={{ fontFamily: 'var(--font-serif)' }}>
                Préambule
              </h2>
              <div className="divider mb-6" />
              <div className="text-theme-secondary leading-relaxed whitespace-pre-line text-lg" style={{ fontFamily: 'var(--font-serif)' }}>
                {constitutionData.preamble.content}
              </div>
            </section>

            {/* Constitution Titles */}
            {constitutionData.titles.map((title) => (
              <section 
                key={title.id}
                id={title.id}
                data-section-id={title.id}
                className={`rounded-xl border p-8 mb-8 scroll-mt-24 ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-[#E8E0D4]'}`}
              >
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-[#7C4DAF]' : 'text-[#5B2D8E]'}`} style={{ fontFamily: 'var(--font-serif)' }}>
                  Titre {title.number}
                </h2>
                <h3 className={`text-xl mb-6 ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`} style={{ fontFamily: 'var(--font-serif)' }}>
                  {title.title}
                </h3>
                <div className="divider mb-6" />

                {/* Direct articles */}
                {title.articles && title.articles.length > 0 && (
                  <div className="space-y-0">
                    {title.articles.map((article) => (
                      <ArticleBlock key={article.id} article={article} />
                    ))}
                  </div>
                )}

                {/* Chapters */}
                {title.chapters && title.chapters.map((chapter) => (
                  <div 
                    key={chapter.id}
                    id={chapter.id}
                    data-section-id={chapter.id}
                    className="mt-8 scroll-mt-24"
                  >
                    <h4 className={`text-lg font-semibold text-theme-primary mb-4 pb-2 border-b ${isDark ? 'border-white/10' : 'border-[#E8E0D4]'}`}>
                      Chapitre {chapter.number} : {chapter.title}
                    </h4>
                    <div className="space-y-0">
                      {chapter.articles.map((article) => (
                        <ArticleBlock key={article.id} article={article} />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}

            {/* Organic Laws Section Header */}
            <div className="bg-[#F5A623]/10 rounded-xl p-6 mb-8 border border-[#F5A623]/30">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#D4880D]" />
                <div>
                  <h2 className="text-2xl font-bold text-[#D4880D]" style={{ fontFamily: 'var(--font-serif)' }}>
                    Lois Organiques d'Application
                  </h2>
                  <p className={`${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>14 lois organiques définissant les modalités d'application</p>
                </div>
              </div>
            </div>

            {/* Organic Laws */}
            {organicLawsData.map((law) => (
              <section 
                key={law.id}
                id={law.id}
                data-section-id={law.id}
                className={`rounded-xl border p-8 mb-8 scroll-mt-24 ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-[#E8E0D4]'}`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <span className="flex-shrink-0 w-12 h-12 bg-[#F5A623]/10 rounded-xl flex items-center justify-center text-[#D4880D] font-bold text-lg">
                    {law.number}
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-theme-primary" style={{ fontFamily: 'var(--font-serif)' }}>
                      Loi Organique N°{law.number}
                    </h2>
                    <h3 className={`text-lg ${isDark ? 'text-stone-400' : 'text-[#6B6B6B]'}`}>{law.title}</h3>
                  </div>
                </div>
                <div className="divider mb-6" />

                {/* Direct articles */}
                {law.articles && law.articles.length > 0 && (
                  <div className="space-y-0">
                    {law.articles.map((article) => (
                      <ArticleBlock key={article.id} article={article} />
                    ))}
                  </div>
                )}

                {/* Chapters (for Law 14) */}
                {law.chapters && law.chapters.map((chapter) => (
                  <div 
                    key={chapter.id}
                    id={chapter.id}
                    data-section-id={chapter.id}
                    className="mt-8 scroll-mt-24"
                  >
                    <h4 className="text-lg font-semibold text-theme-primary mb-4 pb-2 border-b border-[#F5A623]/30">
                      Chapitre {chapter.number} : {chapter.title}
                    </h4>
                    <div className="space-y-0">
                      {chapter.articles.map((article) => (
                        <ArticleBlock key={article.id} article={article} />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}

            {/* Back to top */}
            <div className="text-center py-8 print:hidden">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="btn-secondary"
              >
                Retour en haut
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\:hidden { display: none !important; }
          .print\:bg-white { background: white !important; }
          .print\:text-black { color: black !important; }
          section { break-inside: avoid; page-break-inside: avoid; }
          h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
        }
      `}</style>
    </div>
  );
};

export default ConstitutionPage;
